import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useGiteaSeeds, useSeedRepo } from "@/api/mutations/gitea";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// #215 (E2) / Multi-Seed: "Template nach Gitea seeden" — legt aus einer wählbaren
// Seed-Quelle (im Image gebackene brain-packages/* + optionale Kunden-Quellen aus dem
// Deploy-Bundle) ein Gitea-Template-Repo an, das danach in einem Profil als Seed wählbar ist.
export function SeedRepoDialog({ open, onOpenChange }: Props) {
  const seeds = useGiteaSeeds(open);
  const [source, setSource] = useState<string>("");
  const [repoName, setRepoName] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [overwrite, setOverwrite] = useState(false);
  const mutation = useSeedRepo();

  // Erste Quelle vorauswählen; Repo-Name folgt der Quelle, solange nicht manuell geändert.
  useEffect(() => {
    if (!source && seeds.data && seeds.data.length > 0) setSource(seeds.data[0].name);
  }, [seeds.data, source]);
  useEffect(() => {
    if (!nameTouched && source) setRepoName(source === "default" ? "standard-workspace" : `${source}-workspace`);
  }, [source, nameTouched]);

  function handleSubmit() {
    setServerError(null);
    mutation.mutate(
      { source, repoName: repoName.trim(), overwrite },
      {
        onSuccess: (r) => {
          toast.success(
            r.overwritten
              ? `${r.fullName} überschrieben (${r.fileCount} Dateien)`
              : r.created
                ? `${r.fullName} angelegt (${r.fileCount} Dateien)`
                : `${r.fullName} existiert bereits — unverändert (Häkchen „überschreiben" zum Aktualisieren)`,
          );
          onOpenChange(false);
        },
        onError: (err) => {
          const msg = (err as Error).message;
          setServerError(msg);
          toast.error(`Seed fehlgeschlagen: ${msg}`);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="seed-repo-dialog">
        <DialogHeader>
          <DialogTitle>Workspace-Template nach Gitea seeden</DialogTitle>
          <DialogDescription>
            Legt aus einer mitgelieferten Seed-Quelle ein neues Gitea-Repo an (Skills, CLAUDE.md,
            memory/ …). Existiert das Repo schon, bleibt es unverändert — außer „überschreiben"
            ist gesetzt (dann neu seeden, z.B. um nach einem Update neue Skills zu übernehmen).
            Danach in einem Settings-Profil als Seed wählbar; der Inhalt ist in Gitea frei anpassbar.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1">
          <Label htmlFor="seed-source">Seed-Quelle</Label>
          <Select value={source} onValueChange={setSource}>
            <SelectTrigger data-testid="seed-field-source" id="seed-source">
              <SelectValue placeholder="Quelle wählen" />
            </SelectTrigger>
            <SelectContent>
              {(seeds.data ?? []).map((s) => (
                <SelectItem key={s.name} value={s.name}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {seeds.isLoading && <div className="text-xs text-muted">Quellen werden geladen …</div>}
          {seeds.isError && (
            <div className="text-sm text-red" data-testid="seed-sources-error">
              Seed-Quellen nicht ladbar: {(seeds.error as Error).message}
            </div>
          )}
          {seeds.data && seeds.data.length === 0 && (
            <div className="text-sm text-muted" data-testid="seed-sources-empty">
              Keine Seed-Quellen verfügbar (WORKSPACE_SEED_ROOTS prüfen).
            </div>
          )}
        </div>

        <div className="space-y-1">
          <Label htmlFor="seed-repo-name">Repo-Name</Label>
          <Input
            id="seed-repo-name"
            value={repoName}
            onChange={(e) => {
              setNameTouched(true);
              setRepoName(e.target.value);
            }}
            placeholder="standard-workspace"
            data-testid="seed-field-repoName"
          />
        </div>

        <label className="flex items-start gap-2 text-sm" htmlFor="seed-overwrite">
          <input
            id="seed-overwrite"
            type="checkbox"
            checked={overwrite}
            onChange={(e) => setOverwrite(e.target.checked)}
            className="mt-0.5"
            data-testid="seed-field-overwrite"
          />
          <span className="text-subtext0">
            Überschreiben, falls das Repo schon existiert (löscht + seedet neu aus der Quelle).
            Nötig, um ein bestehendes Template nach einem Update zu aktualisieren. Forks
            bestehender Agenten bleiben unberührt.
          </span>
        </label>

        {serverError && (
          <div className="text-sm text-red" data-testid="seed-server-error">
            {serverError}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="seed-cancel">
            Abbrechen
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={mutation.isPending || repoName.trim().length === 0 || source.length === 0}
            data-testid="seed-submit"
          >
            {mutation.isPending ? "..." : "Seeden"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
